import { ChevronRight, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  CoverageStatus,
  DestinationOption,
  DestinationScope,
  ResearchStatus,
} from '@/types/provider-destinations';

import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  formatLastSuccess,
  SCOPE_LABELS,
} from './utils';
import { VerificationBadge } from './VerificationBadge';

interface DestinationScopeRowProps {
  scope: DestinationScope;
  currentDestination: DestinationOption | null;
  usePlaceholderRequest?: boolean;
  followUpPhone?: string;
  coverageStatus: CoverageStatus;
  researchStatus: ResearchStatus;
  onEdit: () => void;
  scopeBadge?: string;
  className?: string;
}

export const DestinationScopeRow = ({
  scope,
  currentDestination,
  usePlaceholderRequest = false,
  followUpPhone,
  coverageStatus,
  researchStatus,
  onEdit,
  scopeBadge,
  className,
}: DestinationScopeRowProps) => {
  const isResearching = researchStatus === 'IN_PROGRESS';
  const ChannelIcon = currentDestination
    ? CHANNEL_ICONS[currentDestination.channel_type]
    : null;

  return (
    <button
      type="button"
      onClick={onEdit}
      data-coverage-status={coverageStatus}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:bg-zinc-50 active:translate-y-0 active:scale-[0.99]',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-900">
            {SCOPE_LABELS[scope]}
          </span>
          {scopeBadge && (
            <span
              title={scopeBadge}
              className="inline-flex max-w-[16rem] items-center truncate rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-100"
            >
              {scopeBadge}
            </span>
          )}
          {isResearching && (
            <Loader2 className="size-3.5 animate-spin text-blue-500" />
          )}
        </div>

        {currentDestination ? (
          <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
            {ChannelIcon && <ChannelIcon className="size-3" />}
            <span className="truncate">
              {CHANNEL_LABELS[currentDestination.channel_type]}:{' '}
              {currentDestination.value}
            </span>
            <VerificationBadge
              status={currentDestination.verification_status}
              className="text-[10px]"
            />
            {currentDestination.last_success_at !== undefined && (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>
                  {formatLastSuccess(currentDestination.last_success_at)}
                </span>
              </>
            )}
          </div>
        ) : null}

        {!currentDestination && usePlaceholderRequest && (
          <p className="mt-0.5 text-xs text-amber-700">
            No destination selected (placeholder request)
          </p>
        )}

        {followUpPhone?.trim() && (
          <p className="mt-0.5 text-xs text-zinc-500">
            Follow-up phone: {followUpPhone.trim()}
          </p>
        )}
      </div>

      <ChevronRight className="size-4 shrink-0 text-zinc-400 transition-transform duration-200 group-hover:translate-x-0.5" />
    </button>
  );
};
